import { Reducer, useEffect, useReducer, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { isEmpty, isNull, size } from 'lodash';

import {
  allRecordsCql,
  facet,
  facetLimit,
  FacetsDto,
  getLanguageOptions,
  getLocationOptions,
  MultiselectDropdownItem,
  useUrlManager,
} from '@app/components';
import { useLocationsFetch } from '@app/hooks';
import { makeRequest } from '@app/utils';
import { langMessages } from '@app/translations';
import { englishLocaleKey } from '@app/constants';

import {
  defaultUnmappedLimitersValue,
  limitersDefaultValue,
  SearchLimiters,
  UnmappedParsedSearchLimiters,
} from '../constants';

export const sortSearchLimiterOptions = (
  options: MultiselectDropdownItem[],
  locale = englishLocaleKey,
  isLanguageOptions = false
) =>
  [...options].sort((a, b) => {
    const isEnglishLanguage = (value: string) =>
      isLanguageOptions && value.toLowerCase() === 'eng';

    if (isEnglishLanguage(a.value)) {
      return -1;
    }

    if (isEnglishLanguage(b.value)) {
      return 1;
    }

    return a.label.localeCompare(b.label, locale, { sensitivity: 'base' });
  });

const mapSourceTypesToDropdownItems = (sourceTypes: string[] = []) => {
  const options = sourceTypes.map(type => ({
    label: type,
    value: type,
    id: type,
  }));

  return sortSearchLimiterOptions(options);
};

export const useAdvancedSearchLimiters = () => {
  const { $t } = useIntl();
  const { getSearchStateFromUrl } = useUrlManager();
  const [unmappedLimiters, setUnmappedLimiters] =
    useState<UnmappedParsedSearchLimiters>({
      ...defaultUnmappedLimitersValue,
      ...getSearchStateFromUrl().facets,
    });

  const sourceTypesQuery = useQuery(
    ['sourceTypes'],
    () => makeRequest<string[]>({ url: '/opac-inventory/source-types' }),
    { refetchOnMount: false }
  );
  const locationsQuery = useLocationsFetch();
  const facetsQuery = useQuery(
    ['facetQuery'],
    () =>
      makeRequest<FacetsDto>({
        url: `/search/instances/facets?facet=sourceTypes:${facetLimit},items.effectiveLocationId:${facetLimit},languages:${facetLimit}&query=${allRecordsCql}`,
      }),
    {
      refetchOnMount: false,
      select: ({ facets }) => ({
        languages: facets.languages?.values,
        locations: facets['items.effectiveLocationId']?.values,
        sourceTypes: facets.sourceTypes?.values,
      }),
    }
  );

  const sourceType = facetsQuery.data?.sourceTypes?.map(item => item.id);
  const sourceTypeItems = mapSourceTypesToDropdownItems(sourceType);
  const languageItems = sortSearchLimiterOptions(
    getLanguageOptions($t, facetsQuery.data?.languages),
    localStorage.getItem('locale') ?? englishLocaleKey,
    true
  );
  const locationsItems = sortSearchLimiterOptions(
    getLocationOptions(locationsQuery.data, facetsQuery.data?.locations)
  );
  const [searchLimiters, setSearchLimiters] = useReducer<
    Reducer<SearchLimiters, Partial<SearchLimiters>>
  >(
    (prevState, newState) => ({
      ...prevState,
      ...newState,
    }),
    limitersDefaultValue
  );

  useEffect(() => {
    if (
      !isEmpty(sourceTypeItems) &&
      !isEmpty(unmappedLimiters.sourceTypes) &&
      !isNull(unmappedLimiters.sourceTypes)
    ) {
      setSearchLimiters({
        [facet.format]: mapSourceTypesToDropdownItems(
          unmappedLimiters.sourceTypes
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size(sourceTypeItems)]);

  useEffect(() => {
    if (
      languageItems.length &&
      !isEmpty(unmappedLimiters.languages) &&
      !isNull(unmappedLimiters.languages)
    ) {
      setSearchLimiters({
        [facet.language]: getLanguageOptions(
          $t,
          unmappedLimiters.languages.map(code => ({
            id: code,
            totalRecords: 0,
          }))
        ),
      });
    }
  }, [$t, languageItems.length, unmappedLimiters.languages]);

  useEffect(() => {
    if (
      size(locationsItems) &&
      size(locationsQuery.data) &&
      !isEmpty(unmappedLimiters[facet.location])
    ) {
      setSearchLimiters({
        [facet.location]: getLocationOptions(
          locationsQuery.data,
          unmappedLimiters[facet.location]?.map(id => ({
            id,
            totalRecords: 0,
          })),
          $t(langMessages.undefined)
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    locationsItems.length,
    locationsQuery.data,
    unmappedLimiters?.[facet.location],
  ]);

  //Immediately reset state back to default one after setting null for clear all functionality
  useEffect(() => {
    if (isNull(unmappedLimiters.sourceTypes)) {
      setUnmappedLimiters(defaultUnmappedLimitersValue);
    }
  }, [unmappedLimiters]);

  return {
    searchLimiters,
    sourceTypeItems,
    languageItems,
    locationsItems,
    unmappedLimiters,
    areSourceTypesLoading: sourceTypesQuery.isLoading,
    areLanguagesLoading: facetsQuery.isLoading,
    areLocationsLoading: facetsQuery.isLoading ?? locationsQuery.isLoading,
    setSearchLimiters,
    setUnmappedLimiters,
  };
};
